/** Missing info email popup — POST /api/email_missing_info (Angular EmailMissingInfoComponent). */
import { useEffect, useRef, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import {
  fetchEmailTemplate,
  sendMissingInfoEmail,
} from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { useAppSelector } from '@/app/hooks'
import modalStyles from '../../styles/modals.module.css'
import { selectUser } from '@/features/auth/authSlice'
import {
  buildMissingInfoCheckboxPayload,
  missingInfoChildKey,
  normalizeMissingInfoTemplateGroups,
  setMissingInfoPersonalizedMessage,
  toggleMissingInfoChildSelected,
  toggleMissingInfoPersonalizedOpen,
} from '../../utils/emailMissingInfoHelpers'
import type {
  CheckboxState,
  EmailMissingInfoModalProps,
  EmailMissingInfoTemplateGroup,
} from '../../types/modals'

const UNDERWRITER_DEFAULT_FROM = 'apps@libertyrent.com'
const APPLICANT_REPLY_TO_DEFAULT = 'apps@libertyrent.com'
const MAX_ATTACHMENT_MB = 10

const EMPTY_CHECKBOX_STATE: CheckboxState = {
  selected: {},
  personalizedMessages: {},
  personalizedOpen: {},
}

export function EmailMissingInfoModal({
  applicantId,
  applicationAllData,
  applicationData,
  onClose,
  onSuccess,
}: EmailMissingInfoModalProps) {
  const tid = 'application-modal-email-missing-info'

  const currentUser = useAppSelector(selectUser)
  const isUnderwriter = currentUser?.current_role?.role_name === 'ROLE_UNDERWRITER'
  const applicantEmail = applicationData?.user_data?.email ?? ''
  const applicationId =
    applicationAllData?.application_id ?? applicationData?.fk_application_id ?? ''

  const [groups, setGroups] = useState<EmailMissingInfoTemplateGroup[]>([])
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  const [checkboxState, setCheckboxState] = useState<CheckboxState>(EMPTY_CHECKBOX_STATE)
  const [toEmails, setToEmails] = useState(applicantEmail)
  const [separated, setSeparated] = useState<'0' | '1'>('0')
  const [notes, setNotes] = useState('')
  // Angular EmailMissingInfoComponent always overwrites reply_to with apps@libertyrent.com.
  const [replyTo, setReplyTo] = useState(APPLICANT_REPLY_TO_DEFAULT)
  const [fromEmail, setFromEmail] = useState(
    isUnderwriter ? UNDERWRITER_DEFAULT_FROM : '',
  )
  const [attachments, setAttachments] = useState<File[]>([])
  const [sendSms, setSendSms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoadingTemplate(true)
    fetchEmailTemplate('MISSING_APPLICANT_INFO')
      .then((tpl) => {
        if (cancelled) return
        setGroups(normalizeMissingInfoTemplateGroups(tpl))
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const msg = (error as { message?: unknown })?.message
          showError(typeof msg === 'string' && msg ? msg : 'Failed to load email template.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const toggleChild = (groupKey: string, childKey: string) => {
    const group = groups.find((item) => item.key === groupKey)
    const child = group?.children.find((item) => item.key === childKey)
    if (!child) return
    setCheckboxState((prev) => toggleMissingInfoChildSelected(prev, groupKey, child))
  }

  const togglePersonalizedMessage = (
    groupKey: string,
    childKey: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    setCheckboxState((prev) =>
      toggleMissingInfoPersonalizedOpen(prev, groupKey, childKey),
    )
  }

  const setPersonalizedMessage = (
    groupKey: string,
    childKey: string,
    value: string,
  ) => {
    setCheckboxState((prev) =>
      setMissingInfoPersonalizedMessage(prev, groupKey, childKey, value),
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const oversize = files.find((f) => f.size / 1024 / 1024 > MAX_ATTACHMENT_MB)
    if (oversize) {
      showError(`File Size Exceeds Allowable Limit of ${MAX_ATTACHMENT_MB} MB.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setAttachments([])
      return
    }
    setAttachments(files)
  }

  const canSubmit = toEmails.trim().length > 0 && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const checkElements = buildMissingInfoCheckboxPayload(groups, checkboxState)

      const fd = new FormData()
      fd.append('to_emails', encodeURIComponent(toEmails.trim()))
      fd.append('seperated_to_address', separated)
      fd.append('notes', notes)
      fd.append('send_sms', sendSms ? '1' : '')
      if (replyTo.trim()) fd.append('reply_to', encodeURIComponent(replyTo.trim()))
      if (isUnderwriter && fromEmail.trim()) {
        fd.append('from_email', encodeURIComponent(fromEmail.trim()))
      }
      if (applicationId) fd.append('application_id', String(applicationId))
      if (applicantId) fd.append('applicant_id', String(applicantId))
      fd.append('checkbox_data', JSON.stringify(checkElements))
      attachments.forEach((file, i) => {
        fd.append(`attachment[${i}]`, file)
      })
      await sendMissingInfoEmail(fd)
      trackApplicationEvent('application_email_sent', 'email', { email_type: 'missing_info' })
      showSuccess('Mail Sent Successfully!')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to send missing-info email.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]'
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1'
  const radioWrapCls = 'flex items-center gap-6'
  const radioLabelCls =
    'flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer'

  const attachmentLabel =
    attachments.length === 0
      ? 'Choose file...'
      : attachments.length === 1
        ? attachments[0].name
        : `${attachments.length} files selected`

  return (
    <OnScreenModal
      isOpen={true}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      title="Send Applicant Missing Info Email"
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      footer={
        <div
          className={modalStyles.modalFooterActionGroup}
          data-testid="application-modal-email-missing-info-footer"
        >
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={modalStyles.modalActionPrimary}
            data-testid="application-modal-email-missing-info-submit-button"
          >
            {submitting ? 'Sending...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={modalStyles.modalActionSecondary}
            data-testid="application-modal-email-missing-info-cancel-button"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid={`${tid}-body`}>
        <div>
          <label className={labelCls} data-testid="application-modal-email-missing-info-to-label">
            TO<span className="text-red-500">*</span>{' '}
            <span className="font-normal text-xs text-slate-500">
              (For example: email1@gmail.com,email2@gmail.com)
            </span>
          </label>
          <input
            type="text"
            className={inputCls}
            value={toEmails}
            onChange={(e) => setToEmails(e.target.value)}
            placeholder="TO"
            data-testid="application-modal-email-missing-info-to-input"
          />
        </div>

        <div>
          <label className={labelCls} data-testid="application-modal-email-missing-info-send-separately-label">
            Send Separately<span className="text-red-500">*</span>
          </label>
          <div className={radioWrapCls}>
            <label className={radioLabelCls} data-testid="application-modal-email-missing-info-send-separately-yes-label">
              <input
                type="radio"
                name="missing_info_separated"
                checked={separated === '1'}
                onChange={() => setSeparated('1')}
                data-testid="application-modal-email-missing-info-send-separately-yes-radio"
              />
              Yes
            </label>
            <label className={radioLabelCls} data-testid="application-modal-email-missing-info-send-separately-no-label">
              <input
                type="radio"
                name="missing_info_separated"
                checked={separated === '0'}
                onChange={() => setSeparated('0')}
                data-testid="application-modal-email-missing-info-send-separately-no-radio"
              />
              No
            </label>
          </div>
        </div>

        {isUnderwriter && (
          <div>
            <label className={labelCls} data-testid="application-modal-email-missing-info-from-email-label">From Email</label>
            <input
              type="text"
              className={inputCls}
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="From Email"
              data-testid="application-modal-email-missing-info-from-email-input"
            />
          </div>
        )}

        <div>
          <label className={labelCls} data-testid="application-modal-email-missing-info-reply-to-label">Reply To</label>
          <input
            type="email"
            className={inputCls}
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="Reply To"
            data-testid="application-modal-email-missing-info-reply-to-input"
          />
        </div>

        <div>
          <label className={labelCls} data-testid="application-modal-email-missing-info-attachment-label">Attachment</label>
          <div className="flex items-stretch overflow-hidden rounded-md border border-slate-300 bg-white">
            <button
              type="button"
              className="flex-1 truncate px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
              onClick={() => fileInputRef.current?.click()}
              data-testid="application-modal-email-missing-info-attachment-display-button"
            >
              {attachmentLabel}
            </button>
            <button
              type="button"
              className="shrink-0 border-l border-slate-300 bg-[rgb(21_151_255)]/10 px-4 py-2 text-sm font-semibold text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/15"
              onClick={() => fileInputRef.current?.click()}
              data-testid="application-modal-email-missing-info-attachment-upload-button"
            >
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="sr-only"
              onChange={handleFileChange}
              data-testid="application-modal-email-missing-info-attachment-input"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">Max {MAX_ATTACHMENT_MB} MB per file.</p>
        </div>

        {loadingTemplate ? (
          <p className="text-sm text-slate-500 italic">Loading template…</p>
        ) : groups.length > 0 ? (
          <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            {groups.map((g) => (
              <div key={g.key}>
                {g.label && (
                  <p
                    className="mb-2 text-sm font-bold text-slate-800"
                    dangerouslySetInnerHTML={{ __html: g.label }}
                  />
                )}
                <div className="ml-2 space-y-2">
                  {g.children.map((c) => {
                    const childStateKey = missingInfoChildKey(g.key, c.key)
                    const isChecked = !!checkboxState.selected[childStateKey]
                    const isPersonalizedOpen = !!checkboxState.personalizedOpen[childStateKey]
                    return (
                      <div
                        key={c.key}
                        className={modalStyles.missingInfoChildItem}
                        data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}`}
                      >
                        <div className={modalStyles.missingInfoOption}>
                          <input
                            type="checkbox"
                            id={`missing-info-${g.key}-${c.key}`}
                            className={modalStyles.missingInfoCheckbox}
                            checked={isChecked}
                            onChange={() => toggleChild(g.key, c.key)}
                            data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}-checkbox`}
                          />
                          <label
                            htmlFor={`missing-info-${g.key}-${c.key}`}
                            className={modalStyles.missingInfoLabel}
                            data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}-label`}
                            dangerouslySetInnerHTML={{ __html: c.label }}
                          />
                          {c.allowPersonalizedNote ? (
                            <button
                              type="button"
                              className={modalStyles.missingInfoPersonalizeButton}
                              title="Add personalized message"
                              aria-label="Add personalized message"
                              disabled={!isChecked}
                              onClick={(event) =>
                                togglePersonalizedMessage(g.key, c.key, event)
                              }
                              data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}-personalize-button`}
                            >
                              ...
                            </button>
                          ) : null}
                        </div>
                        {isPersonalizedOpen ? (
                          <div className={modalStyles.missingInfoPersonalizedWrap}>
                            <label
                              className="mb-1 block text-xs text-slate-600"
                              htmlFor={`personalized-message-${g.key}-${c.key}`}
                              data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}-personalized-message-label`}
                            >
                              Personalized message (optional)
                            </label>
                            <textarea
                              id={`personalized-message-${g.key}-${c.key}`}
                              className={`${inputCls} min-h-[72px] resize-y`}
                              rows={3}
                              placeholder="Add a note for this item"
                              value={checkboxState.personalizedMessages[childStateKey] ?? ''}
                              onChange={(event) =>
                                setPersonalizedMessage(g.key, c.key, event.target.value)
                              }
                              data-testid={`application-modal-email-missing-info-group-${g.key}-child-${c.key}-personalized-message-input`}
                            />
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div>
          <label className={labelCls} data-testid="application-modal-email-missing-info-additional-message-label">Additional Message</label>
          <CKEditor4 value={notes} onChange={setNotes} />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="application-modal-email-missing-info-send-sms-label">
          <input
            type="checkbox"
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
            data-testid="application-modal-email-missing-info-send-sms-checkbox"
          />
          Send Notification SMS
        </label>

      </div>
    </OnScreenModal>
  )
}
